import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePopup } from '../../assets/contexts/PopupContext';
import { getPopupComponent } from './popupRegistry';
import './Popup.css';

/**
 * Popup 컴포넌트
 * React Portal을 사용하여 body에 렌더링
 * 팝업 레지스트리를 통해 동적으로 팝업 컴포넌트를 로드
 */
const Popup = () => {
  const { popupState, closePopup } = usePopup();
  const { isOpen, type, config } = popupState;

  // ESC 키로 팝업 닫기
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closePopup({ submit: false });
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // body 스크롤 방지
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, closePopup]);

  // 팝업 닫기 핸들러 (각 팝업 컴포넌트에 전달)
  const handleClose = useCallback((submit = false, result = null) => {
    if (submit) {
      closePopup({ submit: true, ...result });
    } else {
      closePopup({ submit: false });
    }
  }, [closePopup]);

  if (!isOpen || !type) return null;

  const container = document.body;
  const PopupComponent = getPopupComponent(type);

  if (!PopupComponent) {
    console.error(`[Popup] 알 수 없는 팝업 타입: ${type}`);
    return null;
  }

  const displayType = config?.displayType || 'modal';

  // SignaturePopup은 자체적으로 컨테이너를 포함하므로 별도 처리
  if (type === 'signature') {
    return createPortal(
      <div className={displayType === 'slide' ? 'popupContainer popupContainer-slide' : 'popupContainer'} onClick={(e) => {
        const closeOnBackdrop = config?.closeOnBackdrop === true;
        if (e.target === e.currentTarget && closeOnBackdrop) {
          handleClose(false);
        }
      }}>
        <PopupComponent config={config} onClose={handleClose} displayType={displayType} />
      </div>,
      container
    );
  }

  // 일반 팝업은 PopupWrapper를 통해 렌더링 (PopupWrapper는 각 팝업 컴포넌트 내부에서 사용)
  return createPortal(
    <PopupComponent config={config} onClose={handleClose} />,
    container
  );
};

export default Popup;
